import React from "react";
import { useLoaderData } from "react-router-dom";

import Location from "@r6digital/rapidstor-types/react/entities/Location";

import {
  CostRetrieveResponse,
  CostRetrieveResponseWithDeal,
} from "@/../e2e/mock/reservation/CostRetrieve";
import { MockOAuthIntegrations } from '@/../e2e/mock/oauth-integrations/MockOAuthIntegrations';
import { ValidUnitList } from "@/../e2e/mock/unit-list/ValidUnitList";
import {
  noAvailableUnitText,
  payLaterButtonText,
  payNowButtonText,
  saveQuoteButtonText,
  showPricesButtonText,
} from "@/core/utils/rapidstor";

import Button from "@/core/components/elements/button";
import OAuthIntegrations from '@/core/components/elements/oauth-integrations';
import Spinner from "@/core/components/elements/spinner";
import JourneyProgress from "@/core/components/journey-progress";
import WizardDescriptor from "@/core/components/wizard/descriptor";
import WizardPricingTable from "@/core/components/wizard/pricing-table";
import DescriptorTag from "@/journey-based-listing/components/descriptor/tag";
import SizeSlider from "@/journey-based-listing/components/SizeSlider";
import Option from "@/journey-based-listing/components/unit-option";

import { firstAvailableUnitType } from "@/features/rapidstor/helpers";

import classes from "@/scss/screens/tests/button.module.scss";

export default function ComponentsTestScreen() {
  const location = useLoaderData() as Location;

  const unitList = ValidUnitList.results;

  return (
    <div
      style={{
        marginTop: 12,
        backgroundColor: "#eee",
        padding: "0 16px",
      }}
    >
      <div className={classes.row}>
        <div className={classes.col}>
          <div className={classes.card}>
            <div className={classes.cardHeader}>Buttons</div>
            <div className={classes.cardBody}>
              <div className={classes.flexDiv}>
                <span>Small Primary</span>
                <Button type="primary" size="small">
                  Test Button
                </Button>
              </div>
              <div className={classes.flexDiv}>
                <span>Medium Primary</span>
                <Button type="primary" size="medium">
                  Test Button
                </Button>
              </div>
              <div className={classes.flexDiv}>
                <span>Small Text On Primary</span>
                <Button type="textOnPrimary" size="small">
                  Test Button
                </Button>
              </div>
              <div className={classes.flexDiv}>
                <span>Small Outline On Primary</span>
                <Button type="outlineOnPrimary" size="small">
                  Test Button
                </Button>
              </div>
              <div className={classes.flexDiv}>
                <span>Small Outline On Background</span>
                <Button type="outlineOnBackground" size="small">
                  Test Button
                </Button>
              </div>
              <div className={classes.flexDiv}>
                <span>Small Primary On White</span>
                <Button type="primaryOnWhite" size="small">
                  Test Button
                </Button>
              </div>
            </div>
          </div>

          <div className={classes.card}>
            <div className={classes.cardHeader}>Loading Animations</div>
            <div className={classes.cardBody}>
              <div className={classes.flexDiv}>
                <span>Spinner</span>
                <Spinner size="50" />
              </div>
              <div className={classes.flexDiv}>
                <span>Spinner Inverted</span>
                <Spinner size="50" inverted />
              </div>
              <div className={classes.flexDiv}>
                <span>Spinner Using Data Attribute Color</span>
                <Spinner size="50" useDataAttribute />
              </div>
            </div>
          </div>

          <div className={classes.card}>
            <div className={classes.cardHeader}>Descriptor Tags</div>
            <div className={classes.cardBody}>
              <div className={classes.flexDiv}>
                <span>Regular</span>
                <DescriptorTag
                  tags={[
                    {
                      icon: "house",
                      content: "Test Content",
                      enabled: true,
                      personas: [],
                    },
                  ]}
                />
              </div>
              <div className={classes.flexDiv}>
                <span>Secondary</span>
                <DescriptorTag
                  secondary
                  tags={[
                    {
                      icon: "house",
                      content: "Test Content",
                      enabled: true,
                      personas: [],
                    },
                  ]}
                />
              </div>
              <div className={classes.flexDiv}>
                <span>Tertiary</span>
                <DescriptorTag
                  tertiary
                  tags={[
                    {
                      icon: "house",
                      content: "Test Content",
                      enabled: true,
                      personas: [],
                    },
                  ]}
                />
              </div>
            </div>
          </div>
        </div>
        <div className={classes.col}>
          <div className={classes.card}>
            <div className={classes.cardHeader}>PopCard Buttons</div>
          </div>

          <div className={classes.card}>
            <div className={classes.cardHeader}>Size Slider</div>
            <div className={classes.cardBody}>
              <div className={classes.slider}>
                <SizeSlider
                  sizes={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10]}
                  activeStep={0}
                  onSetActiveStep={() => {
                    return;
                  }}
                />
              </div>
            </div>
          </div>
          <div className={classes.card}>
            <div className={classes.cardHeader}>RapidStor String Methods</div>
            <div className={classes.cardBody}>
              <div className={classes.flexDiv}>
                <span>Pay Now Button Text</span>
                <span className={classes.well} data-testid="paynowbuttontext">
                  {payNowButtonText(location)}
                </span>
              </div>
              <div className={classes.flexDiv}>
                <span>Pay Later Button Text</span>
                <span className={classes.well} data-testid="paylaterbuttontext">
                  {payLaterButtonText(location)}
                </span>
              </div>
              <div className={classes.flexDiv}>
                <span>Save Quote Button Text</span>
                <span
                  className={classes.well}
                  data-testid="savequotebuttontext"
                >
                  {saveQuoteButtonText(location)}
                </span>
              </div>
              <div className={classes.flexDiv}>
                <span>Show Prices Button Text</span>
                <span
                  className={classes.well}
                  data-testid="showpricesbuttontext"
                >
                  {showPricesButtonText(location)}
                </span>
              </div>
              <div className={classes.flexDiv}>
                <span>No Available Unit Text</span>
                <span
                  className={classes.well}
                  data-testid="noavailablebuttontext"
                >
                  {noAvailableUnitText(location)}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className={classes.col}>
          {unitList?.unitTypes && (
            <div className={classes.card}>
              <div className={classes.cardHeader}>Option Cards</div>
              <div className={classes.cardBody}>
                <div className={classes.fl}>
                  <span>Option Regular</span>
                  <div
                    className={classes.primaryCard}
                    style={{ maxWidth: 500 }}
                  >
                    <Option
                      option={firstAvailableUnitType(
                        unitList?.unitTypes as any,
                        location
                      )}
                      location={location}
                      selected={false}
                      setSelectedID={() => {
                        return;
                      }}
                      theme={`base`}
                    />
                  </div>
                  <span>Option Selected</span>
                  <div
                    className={classes.primaryCard}
                    style={{ maxWidth: 500 }}
                  >
                    <Option
                      option={firstAvailableUnitType(
                        unitList?.unitTypes as any,
                        location
                      )}
                      location={location}
                      selected={true}
                      setSelectedID={() => {
                        return;
                      }}
                      theme={`base`}
                    />
                  </div>
                </div>
                <div className={classes.fl}>
                  <span>Option Total Price</span>
                  <div
                    className={classes.primaryCard}
                    style={{ maxWidth: 500 }}
                  >
                    <Option
                      option={firstAvailableUnitType(
                        unitList?.unitTypes as any,
                        location
                      )}
                      location={location}
                      selected={false}
                      setSelectedID={() => {
                        return;
                      }}
                      showTotalPrice
                      theme={`base`}
                    />
                  </div>
                  <span>Option Wizard</span>
                  <div
                    className={classes.primaryCard}
                    style={{ maxWidth: 500 }}
                  >
                    <Option
                      option={firstAvailableUnitType(
                        unitList?.unitTypes as any,
                        location
                      )}
                      location={location}
                      selected={false}
                      setSelectedID={() => {
                        return;
                      }}
                      wizard
                      theme={`base`}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className={classes.card}>
            <div className={classes.cardHeader}>Journey Progress Bar</div>
            <div className={classes.cardBody}>
              <span>Regular</span>
              <JourneyProgress step={1} stepsType={"regular"} />
              <JourneyProgress step={2} stepsType={"regular"} />
              <JourneyProgress step={3} stepsType={"regular"} />
              <span>Wizard</span>
              <JourneyProgress step={1} stepsType={"reservation-with-fee"} />
              <JourneyProgress step={2} stepsType={"reservation-with-fee"} />
            </div>
          </div>
        </div>
      </div>
      <div className={classes.row}>
        <div className={classes.col}>
          <div className={classes.card}>
            <div className={classes.cardHeader}>Wizard Descriptor Loading</div>
            <div className={classes.cardBody}>
              <WizardDescriptor descriptor={undefined} unitType={undefined} />
            </div>
          </div>
          <div className={classes.card}>
            <div className={classes.cardHeader}>Wizard Descriptor Loading</div>
            <div className={classes.cardBody}>
              <WizardDescriptor
                descriptor={unitList?.descriptors[0] as any}
                unitType={unitList?.unitTypes[0] as any}
              />
            </div>
          </div>
        </div>
      </div>
      <div className={classes.card}>
        <div className={classes.cardHeader}>OAuth Integrations</div>
        <div className={classes.cardBody}>
            <div className={classes.flexDiv}>
                <span>Default OAuth Integrations (Mock Data)</span>
                <div data-testid="oauth-integrations-default">
                    <OAuthIntegrations
                        continueTo="https://test.example.com/callback"
                        metadata={{ testMode: true }}
                        onIntegrationSuccess={(integration, redirectUrl) => {
                            console.log('Integration success:', integration.public_name, redirectUrl);
                        }}
                        onIntegrationError={(integration, error) => {
                            console.log('Integration error:', integration.public_name, error);
                        }}
                    />
                </div>
            </div>
            <div className={classes.flexDiv}>
                <span>Provided OAuth Integrations (Mock Data)</span>
                <div data-testid="oauth-integrations-provided-integrations">
                    <OAuthIntegrations
                        integrations={MockOAuthIntegrations}
                        continueTo="https://test.example.com/callback"
                        metadata={{ testMode: true }}
                        onIntegrationSuccess={(integration, redirectUrl) => {
                            console.log('Integration success:', integration.public_name, redirectUrl);
                        }}
                        onIntegrationError={(integration, error) => {
                            console.log('Integration error:', integration.public_name, error);
                        }}
                    />
                </div>
            </div>
        </div>
    </div>
      <div className={classes.row}>
        <div className={classes.col}>
          <div className={classes.card}>
            <div className={classes.cardHeader}>Wizard Pricing Table</div>
            <div className={classes.cardBody}>
              <WizardPricingTable
                style="horizontal"
                costRetrieve={CostRetrieveResponse.results}
                charges={undefined}
                merchandiseDiscount={0}
              />
            </div>
            <div className={classes.cardBody}>
              <WizardPricingTable
                style="vertical"
                costRetrieve={CostRetrieveResponse.results}
                charges={undefined}
                merchandiseDiscount={0}
              />
            </div>
          </div>
        </div>
      </div>
      <div className={classes.row}>
        <div className={classes.col}>
          <div className={classes.card}>
            <div className={classes.cardHeader}>
              Wizard Pricing Table With Discounts
            </div>
            <div className={classes.cardBody}>
              <WizardPricingTable
                style="horizontal"
                costRetrieve={CostRetrieveResponseWithDeal.results}
                charges={undefined}
                merchandiseDiscount={0}
              />
            </div>
            <div className={classes.cardBody}>
              <WizardPricingTable
                style="vertical"
                costRetrieve={CostRetrieveResponseWithDeal.results}
                charges={undefined}
                merchandiseDiscount={0}
              />
            </div>
          </div>
        </div>
      </div>
      <div className={classes.row}>
        <div className={classes.col}>
          <div className={classes.card}>
            <div className={classes.cardHeader}>
              Vertical Wizard Pricing Table With Tax Row
            </div>
            <div className={classes.cardBody}>
              <WizardPricingTable
                style="vertical"
                costRetrieve={CostRetrieveResponseWithDeal.results}
                charges={undefined}
                merchandiseDiscount={0}
                showTaxRow={true}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
